import React, { useEffect, useState } from "react";
import { AlertTriangle, BookMarked, Check } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import useSessionRecipes from "../../hooks/useSessionRecipes";
import DetailSheet from "../session/generate/DetailSheet";
import ShareLinkDialog from "../share/ShareLinkDialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { DefaultTag, DeleteConfirm, Field, ItemRow, SwitchRow } from "./ProfileParts";
import {
  RECIPE_FIELDS,
  buildRecipeConfig,
  describeRecipe,
  hasBrokenEquipmentLink,
  pinnedFieldsOf,
} from "../../utils/sessionRecipes";

/**
 * Session recipes (issue #78), managed from Profile. Creating one lives in
 * Generate, the moment you've set up a session you'd want again; here you
 * rename, unpin, pick the default, share or delete. A recipe is a named
 * override of How you train, which is why its row sits in that group.
 */
const RecipesSheet = ({ open, onClose }) => {
  const { isAuthenticated } = useAuth();
  const { equipmentPresets } = useUserProfile();
  const { recipes, updateRecipe, deleteRecipe } = useSessionRecipes();

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", pinned: [] });
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  // A recipe shares its parameters, not a fixed workout: the recipient
  // regenerates against their own profile and kit.
  const [sharing, setSharing] = useState(null);

  useEffect(() => {
    if (open) setEditingId(null);
  }, [open]);

  const recipe = recipes.find((r) => r.id === editingId);

  const openRecipe = (r) => {
    setEditingId(r.id);
    setForm({ name: r.name, pinned: pinnedFieldsOf(r) });
    setDirty(false);
    setError(null);
    setConfirming(false);
  };

  const togglePin = (key) => {
    setForm((f) => ({
      ...f,
      pinned: f.pinned.includes(key) ? f.pinned.filter((k) => k !== key) : [...f.pinned, key],
    }));
    setDirty(true);
  };

  // Unpinning is the only value-changing edit here: re-pinning a field to a
  // new value means answering "45 or 30 minutes?", which is Generate's job.
  // So the stored config is filtered down to the fields still ticked.
  const commit = async () => {
    if (!recipe || !dirty) return true;
    if (!form.name.trim()) {
      setError("Give it a name.");
      return false;
    }
    const { config, equipmentPresetId } = buildRecipeConfig(
      recipe.config || {},
      form.pinned,
      equipmentPresets,
    );
    setBusy(true);
    try {
      await updateRecipe(recipe.id, {
        name: form.name.trim(),
        config,
        // The reference survives only while equipment stays pinned.
        equipment_preset_id: form.pinned.includes("availableEquipment")
          ? (recipe.equipment_preset_id ?? equipmentPresetId)
          : null,
      });
      return true;
    } catch (err) {
      console.error("Failed to update session recipe:", err);
      setError("Couldn't save. Check your connection and try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const back = async () => {
    if (await commit()) setEditingId(null);
  };
  const close = async () => {
    if (await commit()) onClose();
  };

  const toggleDefault = (r) =>
    updateRecipe(r.id, { is_default: !r.is_default }).catch((err) =>
      console.error("Failed to set default session recipe:", err),
    );

  const remove = async () => {
    setBusy(true);
    try {
      await deleteRecipe(recipe.id);
      setEditingId(null);
    } catch (err) {
      console.error("Failed to delete session recipe:", err);
      setError("Couldn't delete. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const r = recipe;
    if (await commit()) {
      onClose();
      setSharing(r);
    }
  };

  let title = "Session recipes";
  let description = "Saved from Generate. Anything a recipe doesn't pin follows How you train.";
  let body;

  if (!recipe) {
    body = (
      <>
        {!isAuthenticated && recipes.length > 0 && (
          <p className="text-sm leading-snug text-muted-foreground">
            These recipes are saved on this device. Sign in and they move to your account.
          </p>
        )}
        {recipes.length === 0 ? (
          <p className="text-sm leading-snug text-muted-foreground">
            None yet. In Generate, set up a session the way you like it and save it as a recipe.
          </p>
        ) : (
          <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
            {recipes.map((r) => {
              const summary = describeRecipe(r, equipmentPresets);
              return (
                <ItemRow
                  key={r.id}
                  Icon={hasBrokenEquipmentLink(r, equipmentPresets) ? AlertTriangle : BookMarked}
                  title={r.name}
                  detail={summary.length ? summary.join(" · ") : "Pins nothing"}
                  tag={r.is_default && <DefaultTag />}
                  onClick={() => openRecipe(r)}
                />
              );
            })}
          </div>
        )}
      </>
    );
  } else {
    title = form.name || "Recipe";
    description = "";
    const pinnedKeys = pinnedFieldsOf(recipe);
    body = (
      <>
        <Field label="Name" htmlFor="recipe-name">
          <Input
            id="recipe-name"
            value={form.name}
            onChange={(e) => {
              setForm((f) => ({ ...f, name: e.target.value }));
              setDirty(true);
              setError(null);
            }}
            placeholder="e.g. Lunchtime kettlebell"
          />
        </Field>
        <Field
          label="Pinned"
          hint="Tap a field to stop this recipe setting it. To pin a different value, set it up in Generate and save the recipe again."
        >
          {pinnedKeys.length === 0 ? (
            <p className="text-sm text-muted-foreground">This recipe pins nothing.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {RECIPE_FIELDS.filter((f) => pinnedKeys.includes(f.key)).map((f) => {
                const pinned = form.pinned.includes(f.key);
                return (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={pinned}
                    onClick={() => togglePin(f.key)}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-3 py-2 text-[13px] transition-colors",
                      pinned
                        ? "border-primary bg-primary/10 font-semibold text-primary"
                        : "border-border bg-background text-muted-foreground line-through",
                    )}
                  >
                    {pinned && <Check className="h-3.5 w-3.5" />}
                    {f.label}
                  </button>
                );
              })}
            </div>
          )}
        </Field>
        {hasBrokenEquipmentLink(recipe, equipmentPresets) && (
          <p className="flex items-start gap-1.5 text-sm leading-snug text-yellow-500">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            Its equipment set was deleted, so sessions use your default equipment.
          </p>
        )}
        <SwitchRow
          id="recipe-default"
          label="Default recipe"
          description="Generate opens with it."
          checked={Boolean(recipe.is_default)}
          onCheckedChange={() => toggleDefault(recipe)}
        />
        {isAuthenticated && (
          <Button type="button" variant="outline" size="sm" className="justify-self-start" onClick={share}>
            Share a link
          </Button>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DeleteConfirm
          name={form.name || "this recipe"}
          consequence="Sessions you've already built from it aren't affected."
          confirming={confirming}
          onAsk={() => setConfirming(true)}
          onCancel={() => setConfirming(false)}
          onConfirm={remove}
          busy={busy}
        />
      </>
    );
  }

  return (
    <>
      <DetailSheet
        open={open}
        onOpenChange={(next) => !next && close()}
        title={title}
        description={description}
        showDescription={Boolean(description)}
        onBack={recipe ? back : undefined}
      >
        {body}
      </DetailSheet>
      <ShareLinkDialog
        open={Boolean(sharing)}
        onOpenChange={(next) => !next && setSharing(null)}
        request={sharing ? { kind: "recipe", recipe_id: sharing.id } : null}
        defaultTitle={sharing?.name || ""}
      />
    </>
  );
};

export default RecipesSheet;
